'use client';

import React from 'react';
import styled from 'styled-components';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  color: #101018;
`;

const InputGroup = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #ffffff;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  overflow: hidden;
  transition: border-color 0.2s;
  
  &:focus-within {
    border-color: ${({ theme }) => theme.colors.primary.accent};
    box-shadow: 0 0 0 3px ${({ theme }) => theme.colors.overlay.focus};
  }
  
  &:hover:not(:focus-within) {
    border-color: #9ca3af;
  }
`;

const StyledInput = styled.input`
  flex: 1;
  padding: 9px 16px;
  font-size: 14px;
  color: #111827;
  border: none;
  outline: none;
  text-align: center;
  background: transparent;
  min-width: 60px;
  width: 100%;
  max-width: max-content;
  
  &:disabled {
    background-color: #f3f4f6;
    color: #9ca3af;
    cursor: not-allowed;
  }
  
  /* Remove spin buttons */
  &::-webkit-outer-spin-button,
  &::-webkit-inner-spin-button {
    -webkit-appearance: none;
    margin: 0;
  }
  
  &[type="number"] {
    -moz-appearance: textfield;
  }
`;

const ControlButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  background: #f5f5f5;
  border: none;
  cursor: pointer;
  color: #6b7280;
  font-size: 18px;
  font-weight: 600;
  transition: all 0.2s;
  
  &:hover:not(:disabled) {
    background: #FB6F2A;
    color: #ffffff;
  }
  
  &:active:not(:disabled) {
    background: #D33324;
  }
  
  &:disabled {
    background-color: #f3f4f6;
    color: #d1d5db;
    cursor: not-allowed;
  }
  
  &:focus {
    outline: none;
  }
`;

const ErrorText = styled.span`
  font-size: 13px;
  color: #ef4444;
  margin-top: 4px;
`;

interface NumberInputProps {
  label?: string;
  value: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number;
  disabled?: boolean;
  error?: string;
  helperIcon?: React.ReactNode;
  className?: string;
}

export const NumberInput: React.FC<NumberInputProps> = ({
  label,
  value,
  onChange,
  min,
  max,
  step = 1,
  disabled = false,
  error,
  helperIcon,
  className,
}) => {
  const handleIncrement = () => {
    const newValue = value + step;
    if (max === undefined || newValue <= max) {
      onChange(newValue);
    }
  };

  const handleDecrement = () => {
    const newValue = value - step;
    if (min === undefined || newValue >= min) {
      onChange(newValue);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = parseFloat(e.target.value);
    if (!isNaN(newValue)) {
      if ((min === undefined || newValue >= min) && (max === undefined || newValue <= max)) {
        onChange(newValue);
      }
    }
  };

  const handleInputClick = (e: React.MouseEvent<HTMLInputElement>) => {
    e.currentTarget.select();
  };

  return (
    <Container className={className}>
      {label && (
        <Label>
          {label}
          {helperIcon}
        </Label>
      )}
      <InputGroup>
        <ControlButton
          type="button"
          onClick={handleDecrement}
          disabled={disabled || (min !== undefined && value <= min)}
          aria-label="Decrease"
        >
          −
        </ControlButton>
        <StyledInput
          type="number"
          value={value}
          onChange={handleInputChange}
          onClick={handleInputClick}
          min={min}
          max={max}
          step={step}
          disabled={disabled}
          aria-label={label}
        />
        <ControlButton
          type="button"
          onClick={handleIncrement}
          disabled={disabled || (max !== undefined && value >= max)}
          aria-label="Increase"
        >
          +
        </ControlButton>
      </InputGroup>
      {error && <ErrorText>{error}</ErrorText>}
    </Container>
  );
};

